import React, { Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Header.module.scss';
import { apiService } from '../../services/api';
import { withPlatformFlags, type PlatformFlagsState } from '../../context/PlatformFlagsContext';

const DEFAULT_AVATAR = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200';

interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: string;
  /** When set, the link only renders if the matching platform module is enabled. */
  module?: 'social' | 'tournaments' | 'marketplace';
}

const navItems: NavItem[] = [
  { id: 'home', label: 'Home', to: '/', icon: 'home' },
  { id: 'organizers', label: 'Explore Venues', to: '/customer/organizers', icon: 'explore' },
  { id: 'orders', label: 'My Orders', to: '/customer/orders', icon: 'orders' },
  { id: 'bookings', label: 'My Bookings', to: '/customer/bookings', icon: 'bookings' },
  { id: 'social-feed', label: 'Social', to: '/customer/social/feed', icon: 'social', module: 'social' },
  { id: 'friends', label: 'Connections', to: '/customer/friends', icon: 'friends', module: 'social' },
  { id: 'groups', label: 'My Groups', to: '/customer/branch-groups', icon: 'groups' },
  { id: 'market', label: 'Marketplace', to: '/customer/market', icon: 'marketplace' },
  { id: 'marketplace', label: 'Group Buys', to: '/customer/marketplace', icon: 'marketplace', module: 'marketplace' },
];

// Line icons keyed by nav id — professional in place of the emoji markers.
const navGlyph = (name: string): JSX.Element => {
  const p = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'home': return <svg {...p}><path d="M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10" /></svg>;
    case 'explore': return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="m15 9-2 4-4 2 2-4 4-2Z" /></svg>;
    case 'orders': return <svg {...p}><path d="M6 2 4 6v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6l-2-4Z" /><path d="M4 6h16M9 11h6" /></svg>;
    case 'bookings': return <svg {...p}><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v3M16 3v3M9 14l2 2 4-4" /></svg>;
    case 'social': return <svg {...p}><path d="M21 11.5a8.4 8.4 0 0 1-9 8.3L3 21l1.2-3.6A8.4 8.4 0 1 1 21 11.5Z" /></svg>;
    case 'friends': return <svg {...p}><circle cx="9" cy="8" r="3.2" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M17 8.5a3 3 0 0 1 0 5M18.5 20a6 6 0 0 0-3-5" /></svg>;
    case 'groups': return <svg {...p}><circle cx="8" cy="9" r="3" /><path d="M2 19a6 6 0 0 1 12 0M16 7a3 3 0 0 1 0 6M22 19a6 6 0 0 0-4-5.7" /></svg>;
    case 'marketplace': return <svg {...p}><path d="M3 9h18l-1.5 11H4.5L3 9ZM3 9l1.5-5h15L21 9M8 13h8" /></svg>;
    default: return <svg {...p}><circle cx="12" cy="12" r="9" /></svg>;
  }
};

interface DashboardHeaderState {
  isScrolled: boolean;
  isMobileMenuOpen: boolean;
  activeMenuItem: string;
  isLoggingOut: boolean;
  profileName: string;
  profilePic: string;
  loadingProfile: boolean;
}

function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    return <WrappedComponent {...props} navigate={navigate} />;
  };
}

class DashboardHeader extends Component<any, DashboardHeaderState> {
  private handleScroll: (() => void) | null = null;
  private handleResize: (() => void) | null = null;
  private handleKeyDown: ((e: KeyboardEvent) => void) | null = null;

  constructor(props: any) {
    super(props);
    this.state = {
      isScrolled: false,
      isMobileMenuOpen: false,
      activeMenuItem: '',
      isLoggingOut: false,
      profileName: '',
      profilePic: '',
      loadingProfile: true
    };
  }

  componentDidMount() {
    this.handleScroll = () => {
      const scrolled = window.scrollY > 20;
      if (scrolled !== this.state.isScrolled) {
        this.setState({ isScrolled: scrolled });
      }
    };

    this.handleResize = () => {
      if (window.innerWidth > 1199 && this.state.isMobileMenuOpen) {
        this.setState({ isMobileMenuOpen: false });
      }
    };

    this.handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.setState({ isMobileMenuOpen: false });
      }
    };

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('keydown', this.handleKeyDown);
    this.loadProfileName();
  }

  loadProfileName = async () => {
    try {
      const response = await apiService.getCustomerProfile();
      if (response.success && response.data) {
        const profile = response.data;
        const displayName = profile.displayName ||
          (profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : null) ||
          profile.firstName ||
          profile.email?.split('@')[0] ||
          'User';
        const profilePic = profile.profilePic || profile.profile_pic || DEFAULT_AVATAR;
        this.setState({ profileName: displayName, profilePic, loadingProfile: false });
      } else {
        this.setState({ profileName: 'User', profilePic: DEFAULT_AVATAR, loadingProfile: false });
      }
    } catch (error) {
      console.error('[DashboardHeader] Failed to load profile:', error);
      this.setState({ profileName: 'User', profilePic: DEFAULT_AVATAR, loadingProfile: false });
    }
  };

  componentDidUpdate(_: any, prevState: DashboardHeaderState) {
    if (this.state.isMobileMenuOpen !== prevState.isMobileMenuOpen) {
      document.body.style.overflow = this.state.isMobileMenuOpen ? 'hidden' : '';
    }
  }

  componentWillUnmount() {
    if (this.handleScroll) window.removeEventListener('scroll', this.handleScroll);
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    if (this.handleKeyDown) document.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = '';
  }

  toggleMenu = () => {
    this.setState(prev => ({ isMobileMenuOpen: !prev.isMobileMenuOpen }));
  };

  closeMenu = () => {
    this.setState({ isMobileMenuOpen: false });
  };

  handleMenuItemClick = (menuItem: string) => {
    this.setState({ activeMenuItem: menuItem, isMobileMenuOpen: false });
  };

  handleLogout = async () => {
    this.setState({ isLoggingOut: true });
    try {
      await apiService.unregisterPushToken().catch(() => {});
      await apiService.logout();
      apiService.clearTokens();
      apiService.clearLastChat();
      this.props.navigate('/');
    } catch (error: any) {
      console.error('Logout failed:', error);
      apiService.clearTokens();
      apiService.clearLastChat();
      this.props.navigate('/');
    } finally {
      this.setState({ isLoggingOut: false });
    }
  };

  renderNavLink = (item: NavItem) => {
    const isActive = this.state.activeMenuItem === item.id;
    const cls = `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`;

    if (item.to.startsWith('#')) {
      return (
        <a href={item.to} className={cls} onClick={() => this.handleMenuItemClick(item.id)}>
          {item.label}
        </a>
      );
    }
    return (
      <Link to={item.to} className={cls} onClick={() => this.handleMenuItemClick(item.id)}>
        {item.label}
      </Link>
    );
  };

  renderMobileNavLink = (item: NavItem) => {
    const isActive = this.state.activeMenuItem === item.id;
    const cls = `${styles.mobileNavLink} ${isActive ? styles.mobileNavLinkActive : ''}`;
    const inner = (
      <>
        <span className={styles.mobileNavIcon}>{navGlyph(item.icon)}</span>
        {item.label}
      </>
    );

    if (item.to.startsWith('#')) {
      return (
        <a href={item.to} className={cls} role="menuitem" onClick={() => this.handleMenuItemClick(item.id)}>
          {inner}
        </a>
      );
    }
    return (
      <Link to={item.to} className={cls} role="menuitem" onClick={() => this.handleMenuItemClick(item.id)}>
        {inner}
      </Link>
    );
  };

  // Hide module-gated links (e.g. Social) unless an admin has enabled that module globally.
  getVisibleNavItems = (): NavItem[] => {
    const flags: PlatformFlagsState | undefined = this.props.platformFlags;
    return navItems.filter((item) => !item.module || !!flags?.[item.module]);
  };

  render() {
    const { isScrolled, isMobileMenuOpen, profileName, profilePic, loadingProfile, isLoggingOut } = this.state;
    const visibleNavItems = this.getVisibleNavItems();

    return (
      <>
        <header className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}>
          <div className={styles.headerContainer}>
            {/* Back — the simple "go back" the app was missing. Hidden on Home. */}
            {window.location.pathname !== '/' && (
              <button
                type="button"
                aria-label="Go back"
                onClick={() => { if (window.history.length > 1) this.props.navigate(-1 as any); else this.props.navigate('/'); }}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, marginRight: 6, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
            )}
            {/* Logo */}
            <div className={styles.headerLogo}>
              <Link to="/" className={styles.logo}>
                <img
                  src="/images/chatup24-logo.png"
                  alt="Chatup24"
                  className={styles.logoImage}
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className={styles.headerNav} role="navigation" aria-label="Main navigation">
              <div className={styles.navMenu}>
                <ul>
                  {visibleNavItems.map(item => (
                    <li key={item.id}>{this.renderNavLink(item)}</li>
                  ))}
                </ul>
              </div>
            </nav>

            {/* Desktop Right Actions */}
            <div className={styles.headerCta}>
              {!loadingProfile && profileName && (
                <div className={styles.profileName}>
                  <img
                    src={profilePic || DEFAULT_AVATAR}
                    alt="Profile"
                    className={styles.profilePic}
                    onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_AVATAR; }}
                  />
                  <span className={styles.profileNameText}>{profileName}</span>
                </div>
              )}
              <Link
                to="/profile"
                className={styles.profileButton}
                onClick={() => this.handleMenuItemClick('profile')}
              >
                Profile
              </Link>
              <button
                className={styles.logoutButton}
                onClick={this.handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? 'Logging out…' : 'Logout'}
              </button>
            </div>

            {/* Mobile Hamburger */}
            <button
              className={`${styles.menuToggle} ${isMobileMenuOpen ? styles.isOpen : ''}`}
              onClick={this.toggleMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <span className={styles.hamburgerIcon} />
            </button>
          </div>

          {/* Mobile Overlay */}
          {isMobileMenuOpen && (
            <div
              className={styles.mobileMenuOverlay}
              onClick={this.closeMenu}
              aria-hidden="true"
            />
          )}

          {/* Mobile Drawer */}
          <nav
            className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.isOpen : ''}`}
            id="mobile-menu"
            role="navigation"
            aria-label="Mobile navigation"
            aria-hidden={!isMobileMenuOpen}
          >
            <div className={styles.mobileMenuScroll}>
              <ul className={styles.mobileNavMenu} role="menubar">
                {visibleNavItems.map(item => (
                  <li key={item.id} className={styles.mobileNavItem} role="none">
                    {this.renderMobileNavLink(item)}
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.mobileMenuFooter}>
              {!loadingProfile && profileName && (
                <div className={styles.mobileProfileName}>
                  <img
                    src={profilePic || DEFAULT_AVATAR}
                    alt="Profile"
                    className={styles.mobileProfilePic}
                    onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_AVATAR; }}
                  />
                  <span>{profileName}</span>
                </div>
              )}
              <Link
                to="/profile"
                className={styles.mobileNavLink}
                onClick={() => this.handleMenuItemClick('profile')}
              >
                <span className={styles.mobileNavIcon}>{navGlyph('friends')}</span>
                Profile
              </Link>
              <button
                className={styles.mobileLogoutBtn}
                onClick={this.handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? 'Logging out…' : '🚪 Logout'}
              </button>
            </div>
          </nav>
        </header>
      </>
    );
  }
}

export default withPlatformFlags(withNavigation(DashboardHeader));
